import i18n from 'modules/core/lib/i18n'

import {
  GetStrategyOptions,
  StrategiesFromPayload,
  StrategiesToPayload,
  StrategyItem,
  StrategyType,
} from './interfaces'

export function getStrategyOptions(key: StrategyType): GetStrategyOptions
export function getStrategyOptions(): GetStrategyOptions[]
export function getStrategyOptions(key?: StrategyType): GetStrategyOptions | GetStrategyOptions[] {
  const list = {
    employee_manager: {
      value: 'employee_manager' as const,
      label: i18n.t('workflows.sidepanel.approval.strategies.employee_manager'),
      ui_type: 'none' as const,
    },
    resource_owner: {
      value: 'resource_owner' as const,
      label: i18n.t('workflows.sidepanel.approval.strategies.resource_owner'),
      ui_type: 'none' as const,
    },
    admins: {
      value: 'admins' as const,
      label: i18n.t('workflows.sidepanel.approval.strategies.admins'),
      ui_type: 'none' as const,
    },
    anyone_in_team: {
      value: 'anyone_in_team' as const,
      label: i18n.t('workflows.sidepanel.approval.strategies.anyone_in_team'),
      ui_type: 'team' as const,
    },
    team_leads: {
      value: 'team_leads' as const,
      label: i18n.t('workflows.sidepanel.approval.strategies.team_leads'),
      ui_type: 'team' as const,
    },
    manual_selection: {
      value: 'manual_selection' as const,
      label: i18n.t('workflows.sidepanel.approval.strategies.manual_selection'),
      ui_type: 'manual_selected_employees' as const,
    },
    job_catalog_role: {
      value: 'job_catalog_role' as const,
      label: i18n.t('workflows.sidepanel.approval.strategies.job_catalog_role'),
      ui_type: 'role' as const,
    },
  }
  return key ? list[key] : Object.values(list)
}

export const strategiesFromPayload = <T extends string>(
  payload: StrategiesFromPayload<T>[]
): Record<T, StrategyItem[]> => {
  const generateForIds = (employeeIds?: number[], prefix = ''): StrategyItem[] =>
    employeeIds
      ?.map((employeeId) => ({
        id: `${prefix}manual_selection_${employeeId}`,
        type: 'manual_selection' as const,
        employeeId: employeeId,
      }))
      .filter(Boolean) ?? []

  const generateForSegments = (
    employeeSegments?: Array<{
      name: StrategyType
      input: Record<string, unknown>
    }>,
    prefix = ''
  ): StrategyItem[] =>
    employeeSegments
      ?.map((segment, index) => {
        const strategyType = getStrategyOptions(segment.name)
        if (!strategyType) {
          return null
        }

        const { ui_type } = strategyType
        const { input, name } = segment

        const teamId = 'team_id' in input ? input['team_id'] : undefined
        const roleId = 'role_id' in input ? input['role_id'] : undefined

        const isTeamType = ui_type === 'team' && typeof teamId === 'number'
        const isRoleType = ui_type === 'role' && typeof roleId === 'string'

        const idPrefix = isTeamType ? teamId : isRoleType ? roleId : undefined

        return {
          id: `${prefix}${name}_${idPrefix ? `${idPrefix}_` : ''}${index}`,
          type: name,
          ...(isTeamType ? { teamId } : {}),
          ...(isRoleType ? { roleId } : {}),
        }
      })
      .filter(Boolean) ?? []

  const initalValue: Record<string, StrategyItem[]> = {}

  return payload.reduce((acc, { groupAs, data, prefix, type }) => {
    if (!(groupAs in acc)) {
      acc[groupAs] = []
    }
    const result = type === 'ids' ? generateForIds(data, prefix) : generateForSegments(data, prefix)
    acc[groupAs]?.push(...result)
    return acc
  }, initalValue)
}

export const strategiesToPayload = <T extends Record<string, unknown> = Record<string, unknown>>(
  strategies: StrategiesToPayload<T>[],
  additionalProps: T
): Record<string, unknown> & T => {
  const getEmployeeIds = (inputStrategies: StrategyItem[] = []) =>
    inputStrategies
      .filter((s) => s.type === 'manual_selection')
      .map((s) => s.employeeId)
      .filter((id): id is number => id !== undefined)

  const getSegments = (inputStrategies: StrategyItem[] = []) =>
    inputStrategies
      .filter((s) => s.type !== 'manual_selection')
      .map((strategy) => {
        const { teamId, roleId, type: name } = strategy

        const isTeamType = teamId !== undefined && ['anyone_in_team', 'team_leads'].includes(name)
        const isRoleType = roleId !== undefined && name === 'job_catalog_role'

        return {
          name,
          input: {
            ...(isTeamType ? { team_id: teamId } : {}),
            ...(isRoleType ? { role_id: roleId } : {}),
          },
        }
      })

  const initialValue: Record<string, unknown[]> = {}

  const result = strategies.reduce((acc, { field, data, type }) => {
    const fieldKey = field.toString()
    if (!(fieldKey in acc)) {
      acc[fieldKey] = []
    }
    const value = type === 'ids' ? getEmployeeIds(data) : getSegments(data)
    acc[fieldKey]?.push(...value)
    return acc
  }, initialValue)

  return {
    ...additionalProps,
    ...result,
  }
}
